import { useContext } from 'react';
import React, { useEffect } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext } from './contexts/AuthContext';
import { UIProvider } from './contexts/UIContext';
import { ScenarioProvider } from './contexts/ScenarioContext';

// ✅ ADD IEP IMPORTS
import { IEPProvider } from './contexts/IEPContext';
import IEPDashboard from './pages/IEP_Dashboard';
import IEPTransformations from './pages/Transformations';
import IEPDimensions from './pages/Dimensions';
import IEPRoadmap from './pages/Roadmap';
import IEPSimulations from './pages/Simulations';
import IEPAIOptimizer from './pages/AIOptimizer';
import IEPOrganizationSetup from './pages/OrganizationSetup';

// ✅ ADD DIGITAL TWIN SIMULATOR IMPORT
//import DigitalTwinSimulatorWithStyles from './components/DigitalTwins/DigitalTwinSimulator';

//aiSolArch
import DigitalTwinGenesis from './components/DigitalTwins/DigitalTwinGenesis.jsx';
import Aiplmxml from './components/ai_PLMXML/ai_PLMXML.jsx';
import Aisolarch from './components/ai_SolArch/ai_SolArch.jsx';



// Import the digital twin databases
import { DigitalTwinEnterpriseDB2026 } from './components/DigitalTwins/realworld_digitaltwins.js';
import { DigitalTwinMasterConfig } from './components/DigitalTwins/digitaltwins_flows.js';

// Existing imports
import Navbar from './components/layout/Navbar';
import ROIAnalyzer from './components/ROIAnalyzer';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import LandingPage from './pages/LandingPage';
import ErrorBoundary from './ErrorBoundary';
import Dashboard from './pages/Dashboard';
import useAnalytics from './hooks/useAnalytics';
import CreditPurchase from './components/CreditPurchase';
import EEvolveLanding from './pages/EEvolveLanding';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import RefundPolicy from './pages/RefundPolicy';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentFailure from './components/PaymentFailure';
import CognitiveOrchestrationEngine from './components/CognitiveOrchestrationEngine';
import GCCImpactAnalyzer from './components/GCCImpactAnalyzer';
import ServicesPage from './components/ServicesPage';
import NavigationWithServices from './components/NavigationWithServices';
import PLMVisualization from './components/PLMVisualization';
import CLM_integration_interfaces from './components/CLM_integration_interfaces/CLM_integration_interfaces';

import PLMVisualizationLowres from './components/lowres/PLMVisualizationLowRes';
import transformations96 from './components/IEP/IEPQuantumExperience.jsx';
import './components/IEP/IEPQuantumExperience.css';
import GCCTransformationTracker from './components/gcc/GCCTransformationTracker';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff4081',
      light: '#ff79b0',
      dark: '#c60055',
    },
    background: {
      default: '#f8fbff',
      paper: '#ffffff',
    },
    success: {
      main: '#4caf50',
      light: '#80e27e',
      dark: '#087f23',
    },
    error: {
      main: '#f44336',
      light: '#ff7961',
      dark: '#ba000d',
    },
    info: {
      main: '#2196f3',
      light: '#6ec6ff',
      dark: '#0069c0',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.1)',
    '0px 4px 8px rgba(0,0,0,0.12)',
    '0px 8px 16px rgba(0,0,0,0.15)',
    '0px 16px 24px rgba(0,0,0,0.18)',
    // ... continue with more shadow levels
  ],
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1a237e',
          color: '#ffffff',
          fontSize: '0.875rem',
          fontWeight: 500,
          borderRadius: 8,
          padding: '8px 12px',
          boxShadow: '0px 8px 24px rgba(26, 35, 126, 0.3)',
          border: '1px solid #303f9f',
        },
        arrow: {
          color: '#1976d2',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        thumb: {
          '&.Mui-active': {
            boxShadow: '0 0 0 12px rgba(25, 118, 210, 0.16)',
          },
          '&:hover': {
            boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.16)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
          },
        },
        contained: {
          background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
          '&:hover': {
            background: 'linear-gradient(45deg, #1565c0, #1976d2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 20px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 8px 32px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '12px !important',
          marginBottom: 16,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '16px 0',
          },
        },
      },
    },
  },
});

function RequireAuth({ children }) {
  const { user } = useContext(AuthContext);
  return user ? children : <Navigate to="/login" replace />;
}

export default function App() {
  const { trackEvent, trackError } = useAnalytics();

  useEffect(() => {
    trackEvent('AppLoaded');
  }, [trackEvent]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <UIProvider>
        {/* ✅ ADD IEP PROVIDER */}
        <IEPProvider>
          <ScenarioProvider>
            <ErrorBoundary>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<EEvolveLanding />} />
                <Route path="/landingpage" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/failure" element={<PaymentFailure />} />
                <Route path="/GCCTransform" element={<GCCTransformationTracker />} />
                
                {/* ✅ SERVICES PAGE IS NOW PUBLIC */}
                <Route path="/services" element={
                  <>
                    <Navbar />
                    <ServicesPage />
                  </>
                } />

                {/* ✅ DIGITAL TWIN SIMULATOR - Public Demo Route */}
                <Route path="/digital-twin-demo" element={
                  <>
                    <Navbar />
                    <DigitalTwinGenesis />
                  </>
                } />
				
                {/* ✅ PLMXML Data creation */}
                <Route path="/aiplmxml" element={
                  <>
                    <Navbar />
                    <Aiplmxml />
                  </>
                } />
				
			   {/* ✅ AI Solution Architect */}
                <Route path="/aisolarch" element={
                  <>
                    <Navbar />
                    <Aisolarch />
                  </>
                } />
							
                {/* ✅ ADD IEP ROUTES */}
                <Route path="/iep" element={
                  <RequireAuth>
                    <Navbar />
                    <IEPOrganizationSetup />
                  </RequireAuth>
                } />
                
                <Route path="/iep/dashboard" element={
                  <RequireAuth>
                    <Navbar />
                    <IEPDashboard />
                  </RequireAuth>
                } />
                
                <Route path="/iep/transformations" element={
                  <RequireAuth>
                    <Navbar />
                    <IEPTransformations />
                  </RequireAuth>
                } />
                
                <Route path="/iep/dimensions" element={
                  <RequireAuth>
                    <Navbar />
                    <IEPDimensions />
                  </RequireAuth>
                } />
                
                <Route path="/iep/roadmap" element={
                  <RequireAuth>
                    <Navbar />
                    <IEPRoadmap />
                  </RequireAuth>
                } />
                
                <Route path="/iep/simulations" element={
                  <RequireAuth>
                    <Navbar />
                    <IEPSimulations />
                  </RequireAuth>
                } />
                
                <Route path="/iep/ai-optimizer" element={
                  <RequireAuth>
                    <Navbar />
                    <IEPAIOptimizer />
                  </RequireAuth>
                } />

                {/* Protected routes - with navbar */}
                <Route path="/dashboard" element={
                  <RequireAuth>
                    <Navbar />
                    <Dashboard />
                  </RequireAuth>
                } />
                
                <Route path="/analyzer" element={
                  <RequireAuth>
                    <Navbar />
                    <ROIAnalyzer />
                  </RequireAuth>
                } />
                
                <Route path="/cognitive-engine" element={
                  <RequireAuth>
                    <Navbar />
                    <CognitiveOrchestrationEngine />
                  </RequireAuth>
                } />
                
                <Route path="/gcc-factors" element={
                  <RequireAuth>
                    <Navbar />
                    <GCCImpactAnalyzer />
                  </RequireAuth>
                } />
                
                <Route path="/scenarios" element={
                  <RequireAuth>
                    <Navbar />
                    <ROIAnalyzer />
                  </RequireAuth>
                } />
                
                <Route path="/credits" element={
                  <RequireAuth>
                    <Navbar />
                    <CreditPurchase />
                  </RequireAuth>
                } />
                
                {/* ✅ DIGITAL TWIN SIMULATOR - Protected Production Route */}
                <Route path="/digital-twin-simulator" element={
                  <RequireAuth>
                    <Navbar />
                    <DigitalTwinGenesis />
                  </RequireAuth>
                } />

                {/* Service Pages - Protected Routes */}
                <Route path="/roi-analyzer" element={
                  <RequireAuth>
                    <Navbar />
                    <ROIAnalyzer />
                  </RequireAuth>
                } />
                
                <Route path="/plm-visualization" element={
                  <RequireAuth>
                    <Navbar />
                    <PLMVisualization />
                  </RequireAuth>
                } />

                <Route path="/data-integration" element={
                  <RequireAuth>
                    <Navbar />
                    <CLM_integration_interfaces />
                  </RequireAuth>
                } />
                
                <Route path="/plmVisualizationLowres" element={
                  <RequireAuth>
                    <Navbar />
                    <PLMVisualizationLowres />
                  </RequireAuth>
                } />
                
                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </ErrorBoundary>
          </ScenarioProvider>
        </IEPProvider>
      </UIProvider>
    </ThemeProvider>
  );
}
